import { useState } from "react";
import { useNotify, useRecordContext, useRefresh, useUpdate } from "ra-core";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

import type { Prospect } from "../types";
import { buildRefreshPatch } from "./enrichFromSource";

/**
 * "Refresh from source" — re-pulls the latest Airtable lead record and merges
 * newly-available data into this prospect. Closes the async skip-trace loop:
 * phones that arrived after the prospect was created get pulled in. Only shown
 * for prospects linked to a DealEngine source.
 */
export const ProspectRefreshButton = () => {
  const record = useRecordContext<Prospect>();
  const [update] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [loading, setLoading] = useState(false);

  if (!record?.source_record_id) return null;

  const handleRefresh = async () => {
    setLoading(true);
    try {
      const patch = await buildRefreshPatch(record);
      if (!patch || Object.keys(patch).length === 0) {
        notify("No new data from source yet", { type: "info" });
        return;
      }
      await update(
        "prospects",
        { id: record.id, data: patch, previousData: record },
        { mutationMode: "pessimistic" },
      );
      const phoneNote =
        Array.isArray(patch.phones) && patch.phones.length
          ? ` (${patch.phones.length} phone${patch.phones.length === 1 ? "" : "s"})`
          : "";
      notify(`Refreshed ${Object.keys(patch).length} field(s) from source${phoneNote}`, {
        type: "success",
      });
      refresh();
    } catch (e) {
      console.error("refresh from source failed", e);
      notify("Could not reach the lead source", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={handleRefresh}
      disabled={loading}
      title="Re-pull latest data (incl. skip-traced phones) from the DealEngine lead"
    >
      <RefreshCw className={`size-4 ${loading ? "animate-spin" : ""}`} />
      {loading ? "Refreshing…" : "Refresh from source"}
    </Button>
  );
};
